import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { KeyRound } from "lucide-react";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, post } from "@/lib/api";
import { isTouch } from "@/lib/utils";
import { safeNextPath } from "@/lib/nav";
import { passkeyErrorMessage } from "@/lib/passkeys";

export function Login() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const passkeys = browserSupportsWebAuthn();

  const done = () => navigate(safeNextPath(params.get("next"), window.location.origin), { replace: true });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/login", { password });
      done();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anmeldung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  async function passkey() {
    setBusy(true);
    setError(null);
    try {
      const optionsJSON = await post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/api/passkey/login/options");
      const response = await startAuthentication({ optionsJSON });
      await post("/api/passkey/login", { response });
      done();
    } catch (err) {
      setError(passkeyErrorMessage(err, "Anmeldung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="w-full max-w-xs space-y-4">
        <h1 className="text-lg font-semibold">boehler.dev cockpit</h1>
        {passkeys && (
          <>
            <Button type="button" className="w-full" disabled={busy} onClick={passkey}>
              <KeyRound /> Mit Passkey anmelden
            </Button>
            <div className="flex items-center gap-3 text-xs text-muted-foreground" aria-hidden>
              <span className="h-px flex-1 bg-border" />oder<span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pw">Passwort</Label>
            <Input id="pw" type="password" autoFocus={!isTouch && !passkeys} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" variant={passkeys ? "outline" : "default"} className="w-full" disabled={busy || !password}>Anmelden</Button>
        </form>
      </div>
    </div>
  );
}
